import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlusCircle, CreditCard, Settings } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { OverviewTab } from "@/components/company/dashboard/OverviewTab";
import { JobsTab } from "@/components/company/dashboard/JobsTab";
import { ProfileTab } from "@/components/company/dashboard/ProfileTab";
import { AnalyticsTab } from "./dashboard/AnalyticsTab";
import { JobPostingForm } from "@/components/company/dashboard/JobPostingForm";
import { PlanSelectionDialog } from "@/components/company/dashboard/PlanSelectionDialog";
import { CompanyHeader } from "@/components/company/Header";
import { useJobForm } from "@/components/company/dashboard/job-form/useJobForm";
import { useJobsManagement } from "@/hooks/useJobsManagement";
import { useUser } from "@supabase/auth-helpers-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useSearchParams } from "react-router-dom";
import { useCompanySubscription } from "@/hooks/useCompanySubscription";
import { Badge } from "@/components/ui/badge";

export default function CompanyDashboard() {
  usePageMeta({
    title: "Dashboard Empresa - Data Hire Club",
    description: "Dashboard da empresa",
    noindex: true,
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPlanDialogOpen, setIsPlanDialogOpen] = useState(false);
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);
  const [availableCredits, setAvailableCredits] = useState(0);
  const user = useUser();
  const { toast } = useToast();
  const { handleCreateJob, fetchJobs } = useJobsManagement(user?.id);
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: subscription, refetch: refetchSubscription } = useCompanySubscription();

  const {
    formData,
    isSubmitting,
    handleInputChange,
    handleSubmit,
    resetForm
  } = useJobForm(async (formData) => {
    const result = await handleCreateJob(formData, 'pending_payment');
    return result;
  });

  // Fetch available credits
  const fetchCredits = useCallback(async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from("company_credits")
      .select("credits_remaining")
      .eq("company_id", user.id)
      .gt("credits_remaining", 0);
    
    const total = data?.reduce((sum, r) => sum + (r.credits_remaining || 0), 0) || 0;
    setAvailableCredits(total);
  }, [user?.id]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  // Handle payment/subscription success redirect
  useEffect(() => {
    const payment = searchParams.get("payment");
    const subStatus = searchParams.get("subscription");
    const jobId = searchParams.get("job_id");
    
    if (payment === "success" && jobId) {
      toast({
        title: "Pagamento confirmado! 🎉",
        description: "Sua vaga será ativada em instantes após a confirmação do pagamento.",
      });
      setSearchParams({});
      fetchJobs();
    } else if (subStatus === "success") {
      toast({
        title: "Assinatura ativada! 🎉",
        description: "Seu plano está ativo. Agora você pode publicar vagas dentro do seu limite mensal.",
      });
      setSearchParams({});
      refetchSubscription();
      fetchJobs();
    } else if (payment === "cancelled" || subStatus === "cancelled") {
      toast({
        title: "Operação cancelada",
        description: "Sua vaga foi salva como rascunho. Você pode finalizá-la a qualquer momento.",
        variant: "destructive",
      });
      setSearchParams({});
    }
  }, [searchParams]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    const result = await handleSubmit(e);
    if (result && result.success && result.data?.id) {
      setIsDialogOpen(false);
      setPendingJobId(result.data.id);
      setIsPlanDialogOpen(true);
    }
  };

  const handleCreditUsed = () => {
    fetchCredits();
    refetchSubscription();
    fetchJobs();
    setPendingJobId(null);
  };

  const handleManageSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível abrir o portal de assinatura.",
        variant: "destructive",
      });
    }
  };

  const activeSubscription = subscription?.subscribed ? {
    plan_type: subscription.plan_type!,
    jobs_used: subscription.jobs_used!,
    jobs_limit: subscription.jobs_limit!,
    highlights_used: subscription.highlights_used ?? 0,
    highlights_limit: subscription.highlights_limit ?? 0,
  } : null;

  return (
    <div className="min-h-screen flex flex-col">
      <CompanyHeader />
      <div className="container mx-auto px-4 py-6 md:py-8 flex-1">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 md:mb-8 gap-4 md:gap-0">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold">Dashboard Empresarial</h1>
            {activeSubscription && (
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary" className="bg-primary/10 text-primary">
                  Plano {activeSubscription.plan_type === "plus" ? "Plus" : "Core"}
                </Badge>
                 <span className="text-sm text-muted-foreground">
                   {activeSubscription.jobs_limit - activeSubscription.jobs_used} vaga{activeSubscription.jobs_limit - activeSubscription.jobs_used !== 1 ? "s" : ""} · {(activeSubscription.highlights_limit || 0) - (activeSubscription.highlights_used || 0)} destaque{((activeSubscription.highlights_limit || 0) - (activeSubscription.highlights_used || 0)) !== 1 ? "s" : ""}
                 </span>
                {subscription?.current_period_end && (
                  <span className="text-xs text-muted-foreground">
                    · Renova em {new Date(subscription.current_period_end).toLocaleDateString("pt-BR")}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2 w-full md:w-auto">
            {activeSubscription && (
              <Button variant="outline" size="sm" onClick={handleManageSubscription} className="text-xs md:text-sm">
                <Settings className="mr-1 h-3 w-3 md:h-4 md:w-4" />
                Gerenciar Assinatura
              </Button>
            )}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90 flex-1 md:flex-none text-sm md:text-base">
                  <PlusCircle className="mr-2 h-3 w-3 md:h-4 md:w-4" />
                  Publicar Nova Vaga
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl w-[95%] p-4 md:p-6">
                <JobPostingForm
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleSubmit={handleFormSubmit}
                  onCancel={() => {
                    setIsDialogOpen(false);
                    resetForm();
                  }}
                  isSubmitting={isSubmitting}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="bg-primary/10 inline-flex w-auto">
            <TabsTrigger value="overview" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground whitespace-nowrap text-xs md:text-sm">
              Visão Geral
            </TabsTrigger>
            <TabsTrigger value="jobs" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground whitespace-nowrap text-xs md:text-sm">
              Minhas Vagas
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground whitespace-nowrap text-xs md:text-sm">
              Analytics
            </TabsTrigger>
            <TabsTrigger value="profile" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground whitespace-nowrap text-xs md:text-sm">
              Perfil da Empresa
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <OverviewTab />
          </TabsContent>
          <TabsContent value="jobs" className="mt-6">
            <JobsTab />
          </TabsContent>
          <TabsContent value="analytics" className="mt-6">
            <AnalyticsTab />
          </TabsContent>
          <TabsContent value="profile" className="mt-6">
            <ProfileTab />
          </TabsContent>
        </Tabs>
      </div>

      {pendingJobId && (
        <PlanSelectionDialog
          open={isPlanDialogOpen}
          onOpenChange={(open) => {
            setIsPlanDialogOpen(open);
            if (!open) setPendingJobId(null);
          }}
          jobId={pendingJobId}
          availableCredits={availableCredits}
          onUseCredit={handleCreditUsed}
          activeSubscription={activeSubscription}
        />
      )}
    </div>
  );
}
